// ---------------------------------------------------------------------------
// FC4 §B.6 — boundary global. Erro de render nunca vira tela branca.
//
// Boundaries por área pesada (mapa do Routing, dashboards) entram SOB DEMANDA,
// não preventivamente. Este é o da raiz.
//
// Boundary de React só captura erro de RENDER (e de lifecycle/efeito síncrono).
// Rejeição de promise em handler — que é onde vive o `ApiError` — não passa por
// aqui; isso é do `notifyApiError`.
// ---------------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';

import {
   BoundaryPage,
   BoundaryCard,
   BoundaryTitle,
   BoundaryText,
   BoundaryCode,
   BoundaryActions,
} from './styled';
// Nenhuma das duas ações escreve no banco (FC7 §G.1): recarregar é a ação de
// destaque (CTA escuro), voltar ao início é secundária (ghost).
import { CtaButton, GhostButton } from '../../styles/ui/Button';

export default class ErrorBoundary extends React.Component {
   constructor(props) {
      super(props);
      this.state = { hasError: false, errorId: null };
   }

   static getDerivedStateFromError(error) {
      // Se o que estourou no render foi um ApiError (5xx propagado por um
      // componente que lança em vez de tratar), o errorId vira código de
      // suporte na tela — mesma regra do toast (§B.7).
      return { hasError: true, errorId: error?.errorId ?? null };
   }

   componentDidCatch(error, info) {
      // Sem serviço de telemetria no front hoje; o console é o que existe.
      // Não engolir: silêncio aqui é o §B.8.
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info?.componentStack);
   }

   handleReload = () => {
      window.location.reload();
   };

   handleGoHome = () => {
      window.location.href = '/main';
   };

   render() {
      const { hasError, errorId } = this.state;
      const { children } = this.props;

      if (!hasError) return children;

      return (
         <BoundaryPage>
            <BoundaryCard>
               <BoundaryTitle>Algo deu errado nesta tela</BoundaryTitle>
               <BoundaryText>
                  A página não conseguiu ser exibida. Seus dados não foram
                  perdidos — recarregar costuma resolver.
               </BoundaryText>

               {errorId ? (
                  <BoundaryText>
                     Se acionar o suporte, informe o código:{' '}
                     <BoundaryCode>{errorId}</BoundaryCode>
                  </BoundaryText>
               ) : null}

               <BoundaryActions>
                  <GhostButton type="button" onClick={this.handleGoHome}>
                     Ir para o início
                  </GhostButton>
                  <CtaButton type="button" onClick={this.handleReload}>
                     Recarregar
                  </CtaButton>
               </BoundaryActions>
            </BoundaryCard>
         </BoundaryPage>
      );
   }
}

ErrorBoundary.propTypes = {
   children: PropTypes.node,
};

ErrorBoundary.defaultProps = {
   children: null,
};
